import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../utils/contexts/UserContext';
import { Button, Col, Container, Row } from 'react-bootstrap';
import {
  ConfirmationModal,
  MessageModal,
  Selection,
} from '../../../components';
import { Card, DatePicker, Descriptions, Table } from 'antd';
import { invoiceTableHeader } from '../../../utils/tableData/InvoiceTableHeader';
import { saveInvoices } from '../../../utils/api/InvoiceAPI';

export default function InvoiceForApproval() {
  const {
    profile,
    invoicesForApproval,
    loadInvoicesForApproval,
    buildings,
    loadBuildings,
  } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [localInvoicesList, setLocalInvoicesList] = useState([]);
  const [localBuildingsList, setLocalBuildingsList] = useState([]);

  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedFromDate, setSelectedFromDate] = useState('');
  const [selectedToDate, setSelectedToDate] = useState('');

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageModalBody, setMessageModalBody] = useState('');

  const data = localInvoicesList.map((data, index) => {
    return { ...data, key: index };
  });

  const getItemRateTotal = () => {
    const total = localInvoicesList
      .filter((item) => item.itemRate !== null && item.itemRate.includes(','))
      .reduce(
        (acc, item) => acc + parseFloat(item.itemRate.replace(/,/g, '')),
        0
      );
    return total.toLocaleString('en-US', { style: 'decimal' });
  };

  const getItemAmountTotal = () => {
    const total = localInvoicesList
      .filter(
        (item) => item.itemAmount !== null && item.itemAmount.includes(',')
      )
      .reduce(
        (acc, item) => acc + parseFloat(item.itemAmount.replace(/,/g, '')),
        0
      );
    return total.toLocaleString('en-US', { style: 'decimal' });
  };

  const getItemTaxAmountTotal = () => {
    const total = localInvoicesList
      .filter(
        (item) =>
          item.itemTaxAmount !== null && item.itemTaxAmount.includes(',')
      )
      .reduce(
        (acc, item) => acc + parseFloat(item.itemTaxAmount.replace(/,/g, '')),
        0
      );
    return total.toLocaleString('en-US', { style: 'decimal' });
  };

  const getItemGrossAmountTotal = () => {
    const total = localInvoicesList
      .filter(
        (item) =>
          item.itemGrossAmount !== null && item.itemGrossAmount.includes(',')
      )
      .reduce(
        (acc, item) => acc + parseFloat(item.itemGrossAmount.replace(/,/g, '')),
        0
      );
    return total.toLocaleString('en-US', { style: 'decimal' });
  };

  const handleApprove = async () => {
    setIsLoading(true);
    setIsConfirmationModalOpen(false);

    let invoicesStatus = [];

    localInvoicesList.forEach((record) => {
      const recordMatched = selectedRows.find(
        (selected) => selected.ledgerId === record.ledgerId
      );
      if (recordMatched !== null && recordMatched !== undefined) {
        const dataRecord = {
          ledgerId: recordMatched.ledgerId,
          invBuilding: recordMatched.invBuilding,
          invoiceNumber: recordMatched.invoiceNo,
          externalId: recordMatched.ExternalID,
          month: recordMatched.month,
          ledgerDate: recordMatched.ledgerDate,
          status: 1,
        };
        invoicesStatus.push(dataRecord);
      }
      // else {
      //   const dataRecord = {
      //     ledgerId: record.ledgerId,
      //     invBuilding: record.invBuilding,
      //     invoiceNumber: record.invoiceNo,
      //     externalId: record.ExternalID,
      //     month: record.month,
      //     ledgerDate: record.ledgerDate,
      //     status: 2,
      //   };
      //   invoicesStatus.push(dataRecord);
      // }
    });

    const response = await saveInvoices(
      parseInt(profile.userId),
      invoicesStatus
    );

    if (response.isSuccessful) {
      resetData();
      setMessageModalBody(
        `${invoicesStatus.length} records(s) out of ${localInvoicesList.length} is approved.`
      );
      setIsMessageModalOpen(true);
    } else {
      setMessageModalBody(
        'An error occured. Please contact system administrator immediately.'
      );
      setIsMessageModalOpen(true);
    }

    setIsLoading(false);
  };

  const resetData = () => {
    setSelectedBuilding('');
    setSelectedFromDate('');
    setSelectedToDate('');

    setLocalInvoicesList([]);
    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    setLocalInvoicesList(invoicesForApproval);
  }, [invoicesForApproval]);

  useEffect(() => {
    setLocalBuildingsList(buildings);
  }, [buildings]);

  useEffect(() => {
    if (buildings.length === 0) {
      loadBuildings();
    }
  }, []);

  return (
    <Container className="pt-3">
      <h2>Invoices - For Approval</h2>
      <hr />
      <Row>
        <Col xs={3}>
          <Selection
            title={'Building'}
            disabled={isLoading}
            value={selectedBuilding}
            onChange={(e) => {
              setSelectedBuilding(e.target.value);
            }}
            options={localBuildingsList.map((building) => ({
              value: building.buildingCode,
              name: building.buildingName,
            }))}
          />
        </Col>
        <Col>
          <DatePicker.RangePicker
            onChange={(dates) => {
              const [start, end] = dates;
              setSelectedFromDate(start.format('YYYY-MM-DD'));
              setSelectedToDate(end.format('YYYY-MM-DD'));
            }}
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
            style={{ height: '40px', width: '100%' }}
            disabled={isLoading}
          />
        </Col>
        <Col>
          <Button
            variant="secondary"
            disabled={
              isLoading ||
              selectedBuilding === '' ||
              selectedFromDate === '' ||
              selectedToDate === ''
            }
            onClick={async () => {
              setIsLoading(true);
              setSelectedRows([]);
              setSelectedRowKeys([]);
              await loadInvoicesForApproval(
                selectedFromDate,
                selectedToDate,
                selectedBuilding
              );
              setIsLoading(false);
            }}
            className="mb-3 w-100"
          >
            {isLoading ? 'Loading...' : 'Filter'}
          </Button>
        </Col>
        <Row className="pe-0">
          <Col className="w-100 text-center pe-0">
            {selectedRows.length > 0 && (
              <Button
                variant="primary"
                disabled={isLoading}
                onClick={() => setIsConfirmationModalOpen(true)}
                className="mb-3 w-100 pms-bg"
              >
                <strong>{isLoading ? 'Saving...' : 'Approve!'}</strong>
              </Button>
            )}
          </Col>
        </Row>
      </Row>
      <Table
        columns={invoiceTableHeader}
        dataSource={data}
        title={() => 'For Approval'}
        size="small"
        bordered="true"
        scroll={{ x: true }}
        loading={isLoading}
        rowSelection={{
          selectedRowKeys: selectedRowKeys,
          onChange: (newSelectedRowKeys, newSelectedRows) => {
            setSelectedRows(newSelectedRows);
            setSelectedRowKeys(newSelectedRowKeys);
          },
        }}
      />
      {localInvoicesList.length !== 0 && (
        <Row>
          <Card
            loading={isLoading}
            className="mt-2"
            hoverable
            title="Total details"
          >
            <Descriptions
              bordered
              layout="vertical"
              size="small"
              column={{ xxl: 4, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
            >
              <Descriptions.Item label="Item: Rate">
                {getItemRateTotal()}
              </Descriptions.Item>
              <Descriptions.Item label="Item: Amount">
                {getItemAmountTotal()}
              </Descriptions.Item>
              <Descriptions.Item label="Item: Tax Amount">
                {getItemTaxAmountTotal()}
              </Descriptions.Item>
              <Descriptions.Item label="Item: Gross Amount">
                {getItemGrossAmountTotal()}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Row>
      )}
      <ConfirmationModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        title={'Confirm Invoices approval.'}
        body={
          'NOTE: All checked rows will be APPROVED and will be uploadable to NetSuite.'
        }
        handleConfirm={handleApprove}
      />

      <MessageModal
        isModalOpen={isMessageModalOpen}
        setIsModalOpen={setIsMessageModalOpen}
        title={'Invoice Approval'}
        body={messageModalBody}
      />
    </Container>
  );
}
